/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { CurrencyInput } from 'foundations-components/transitional/components/CurrencyInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getUserRequestedRefundReasons } from '../../../constants/refundReasons';
import { useRefundProperties } from '../../../hooks/useRefundProperties';
import { useRefundTransactionMutation } from '../../../hooks/useRefundTransactionMutation';
import { usageTracker } from '../../../utils/usageTracker';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function showSuccessAlert() {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundSuccessAlert"
    }),
    type: 'success'
  });
}
function showErrorAlert() {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundError.genericAlert"
    }),
    type: 'danger'
  });
}
function RefundModal({
  objectId,
  paymentSource,
  onRefundSuccess,
  onClose
}) {
  const [refundAmount, setRefundAmount] = useState(undefined);
  const [refundReason, setRefundReason] = useState('');
  const {
    isRefundable,
    currencyCode,
    maxRefundableAmount,
    canEditRefundAmount
  } = useRefundProperties(objectId);
  useEffect(() => {
    if (maxRefundableAmount !== undefined) {
      setRefundAmount(maxRefundableAmount);
    }
  }, [maxRefundableAmount]);
  const handleRefundModalClose = useCallback(() => {
    setRefundAmount(0);
    setRefundReason('');
    onClose();
  }, [setRefundAmount, setRefundReason, onClose]);
  const handleRefundModalCancel = useCallback(() => {
    usageTracker.track('paymentCRMSidebarInteraction', {
      action: 'refundModalCancelClicked'
    });
    handleRefundModalClose();
  }, [handleRefundModalClose]);
  const [refundTransaction, {
    loading
  }] = useRefundTransactionMutation({
    paymentObjectId: +objectId,
    refundAmount: refundAmount,
    // Guaranteed to have a value because submit btn is disabled unless refundAmount truthy
    refundReason,
    onError: showErrorAlert,
    onSuccess() {
      usageTracker.track('paymentCRMSidebarUsage', {
        action: 'refundModalSubmitClicked'
      });
      showSuccessAlert();
      onRefundSuccess();
      handleRefundModalClose();
    }
  });
  const handleRefund = useCallback(() => {
    refundTransaction();
  }, [refundTransaction]);
  return /*#__PURE__*/_jsx(Modal, {
    "data-test-id": "refund-modal",
    children: /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.header"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: handleRefundModalClose
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [paymentSource !== 'INTEGRATION' && /*#__PURE__*/_jsx(Text, {
          marginBlockEnd: "600",
          as: "p",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.timeline.default"
          })
        }), maxRefundableAmount !== undefined && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsxs("strong", {
            "data-test-id": "max-refundable-amount",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.maxRefundableAmount"
            }), /*#__PURE__*/_jsx(FormattedIntlCurrency, {
              value: maxRefundableAmount,
              currencyCode: currencyCode
            })]
          })
        }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormControl, {
          required: true,
          label: I18n.text('cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundAmount'),
          labelTooltip: !canEditRefundAmount ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundAmountRemainingBalance"
          }) : null,
          children: /*#__PURE__*/_jsx(CurrencyInput, {
            "data-test-id": "refund-amount-input",
            autoFocus: true,
            min: 0,
            max: maxRefundableAmount,
            value: refundAmount,
            currency: currencyCode,
            disabled: !canEditRefundAmount,
            onChange: evt => {
              if (evt.target.value === null || evt.target.value === undefined) {
                return setRefundAmount(undefined);
              }
              return setRefundAmount(parseFloat(evt.target.value));
            },
            placeholder: I18n.formatIntlCurrency(maxRefundableAmount || 0, {
              currencyCode
            })
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          required: true,
          label: I18n.text('cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundReason'),
          children: /*#__PURE__*/_jsx(Select, {
            "data-test-id": "refund-reason-input",
            value: refundReason,
            onChange: evt => setRefundReason(evt.target.value),
            options: getUserRequestedRefundReasons(),
            placeholder: I18n.text('cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundReasonPlaceholder')
          })
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": "submit-refund-request",
          use: "primary",
          onClick: handleRefund,
          loading: loading,
          disabled: !refundAmount || !refundReason || !isRefundable,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refund"
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: handleRefundModalCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.cancel"
          })
        })]
      })]
    })
  });
}
RefundModal.propTypes = {
  objectId: PropTypes.string.isRequired,
  onRefundSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
export default RefundModal;